import LAYOUT_API from "@frontend/services/api/layout";
import {
  editorControlEventBroker,
  EVENTS
} from "@frontend/group/modules/editor-control-popup/event-broker";
import store from "@frontend/store";
import {addAddField, DEFAULT_TEXT_FIELD_PROPERTIES, DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES} from "@frontend/group/modules/additional-fields";
import parseText from "@/common/preview/parse-text";
import { fabric } from "fabric";

export const LOADING_TYPE = {
  COPY_AND_TRANSLATE: 'loadingCopyAndTranslate', 
  COPY_FIELDS_AND_TRANSLATE: 'loadingCopyFieldsAndTranslate',
  TRANSLATE: 'loadingTranslate'
}

export const HANDLER_TYPES = {
  COPY_AND_TRANSLATE: 'COPY_AND_TRANSLATE',
  COPY_FIELDS_AND_TRANSLATE: 'COPY_FIELDS_AND_TRANSLATE',
  TRANSLATE: 'TRANSLATE',
} 

const HANDLERS = {
  COPY_AND_TRANSLATE: copyAndTranslate, 
  COPY_FIELDS_AND_TRANSLATE: copyFieldsAndTranslate,
  TRANSLATE: translateWithoutReload,
}

function normalizeTranslateTextAndStyles(element) {
  const hasInlineMarkup = /<c\s+['"][^'"]+['"]>|<u>|<sup>|<sub>/i.test(element.text || '');

  if (!hasInlineMarkup) {
    return {
      text: element.text,
      styles: element.styles || null,
    };
  }

  const parsed = parseText(element.text, element.fontColor || element.fill || '#000000');

  return {
    text: parsed.text,
    styles: parsed.styles || element.styles || null,
  };
}

function normalizeFabricStyles(styles) {
  if (!Array.isArray(styles)) {
    return null;
  }

  return styles.map(line => {
    if (!Array.isArray(line)) {
      return [];
    }

    return line.map(style => {
      if (style && typeof style === 'object' && !Array.isArray(style)) {
        return style;
      }

      return {};
    });
  });
}

function rgbToHex(value) {
  const match = String(value || '').match(/rgb\s*\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/i);
  if (!match) {
    return value || null;
  }

  return '#' + [match[1], match[2], match[3]]
    .map(part => Number(part).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

function parseStyleAttribute(styleText) {
  const style = {};

  String(styleText || '').split(';').forEach(part => {
    const [rawKey, rawValue] = part.split(':');
    if (!rawKey || !rawValue) {
      return;
    }

    const key = rawKey.trim();
    const value = rawValue.trim();

    if (key === 'fill') {
      style.fill = rgbToHex(value);
    } else if (key === 'font-size') {
      const fontSize = parseFloat(value);
      if (!Number.isNaN(fontSize)) {
        style.fontSize = fontSize;
      }
    } else if (key === 'font-family') {
      style.fontFamily = value.replace(/^['"]|['"]$/g, '');
    } else if (key === 'font-weight') {
      style.fontWeight = value;
    } else if (key === 'font-style') {
      style.fontStyle = value;
    }
  });

  return style;
}

function getTspanStyle(tspan, element) {
  const style = parseStyleAttribute(tspan.getAttribute('style'));
  const fill = tspan.getAttribute('fill') || style.fill || element.fill;
  const fontSize = tspan.getAttribute('font-size') || style.fontSize || element.fontSize;

  return {
    ...(fill && { fill: rgbToHex(fill) }),
    ...(fontSize && { fontSize: Number(fontSize) || fontSize }),
    ...(style.fontFamily && { fontFamily: style.fontFamily }),
    ...(style.fontWeight && { fontWeight: style.fontWeight }),
    ...(style.fontStyle && { fontStyle: style.fontStyle }),
  };
}

function buildSvgStyleRuns(element) {
  if (typeof element.toSVG !== 'function' || typeof DOMParser === 'undefined') {
    return [];
  }

  try {
    const svg = element.toSVG();
    const document = new DOMParser().parseFromString(`<svg>${svg}</svg>`, 'image/svg+xml');
    const tspans = Array.from(document.querySelectorAll('tspan'));
    const lineIndexes = new Map();

    return tspans.map((tspan, index) => {
      const y = tspan.getAttribute('y') || String(index);
      if (!lineIndexes.has(y)) {
        lineIndexes.set(y, lineIndexes.size);
      }

      return {
        run_id: String(index),
        line: lineIndexes.get(y),
        text: tspan.textContent || '',
        style: getTspanStyle(tspan, element),
        x: tspan.getAttribute('x'),
        y,
      };
    }).filter(run => run.text.length > 0);
  } catch (error) {
    return [];
  }
}

function getElementTextLines(element, text) {
  if (Array.isArray(element._textLines) && element._textLines.length > 0) {
    return element._textLines.map(line => Array.isArray(line) ? line.join('') : String(line));
  }

  return String(text || '').split(/\r?\n/);
}

function buildLineStyleRuns(lineText, lineStyles) {
  const chars = Array.from(lineText || '');
  const styles = Array.isArray(lineStyles) ? lineStyles : [];
  const runs = [];
  let currentRun = null;
  let currentKey = null;

  chars.forEach((char, index) => {
    const style = styles[index] || {};
    const key = JSON.stringify(style);

    if (!currentRun || key !== currentKey) {
      currentRun = {
        start: index,
        end: index,
        style,
      };
      runs.push(currentRun);
      currentKey = key;
    } else {
      currentRun.end = index;
    }
  });

  return runs;
}

function buildLineStyleRunsFromLayoutRuns(lineText, runs) {
  const styleRuns = [];
  let cursor = 0;
  let previousStyle = null;

  runs.forEach(run => {
    const runText = String(run.text || '');
    if (!runText) {
      return;
    }

    let start = String(lineText).indexOf(runText, cursor);
    if (start === -1) {
      start = cursor;
    }

    if (start > cursor && previousStyle) {
      styleRuns.push({
        start: cursor,
        end: start - 1,
        style: previousStyle,
      });
    }

    const end = Math.min(start + Array.from(runText).length - 1, Array.from(lineText).length - 1);
    if (end >= start) {
      styleRuns.push({
        start,
        end,
        style: run.style || {},
      });
      previousStyle = run.style || {};
      cursor = end + 1;
    }
  });

  if (cursor < Array.from(lineText).length && previousStyle) {
    styleRuns.push({
      start: cursor,
      end: Array.from(lineText).length - 1,
      style: previousStyle,
    });
  }

  return styleRuns;
}

function buildLayoutLines(element, text, styles, svgStyleRuns = []) {
  const textLines = getElementTextLines(element, text);
  const normalizedStyles = normalizeFabricStyles(styles) || [];
  const runsByLine = svgStyleRuns.reduce((carry, run) => {
    if (!carry[run.line]) {
      carry[run.line] = [];
    }

    carry[run.line].push(run);
    return carry;
  }, {});

  return textLines.map((lineText, lineIndex) => ({
    line: lineIndex,
    text: lineText,
    style_runs: runsByLine[lineIndex]
      ? buildLineStyleRunsFromLayoutRuns(lineText, runsByLine[lineIndex])
      : buildLineStyleRuns(lineText, normalizedStyles[lineIndex] || []),
  })).filter(line => line.text.length > 0);
}

function buildTranslateFieldObjects(instance, translateFields) {
  const preview = store.getters['layout/selectedInstance'] || instance;
  const canvas = preview?.canvas;

  if (!canvas || !Array.isArray(translateFields)) {
    return {};
  }

  const fieldSet = new Set(translateFields);
  const fieldObjects = {};

  canvas.getObjects('textbox').forEach((element) => {
    if (!element.element_id || !fieldSet.has(element.element_id)) {
      return;
    }

    const data = {};
    Object.keys(preview.data || {}).forEach((key) => {
      if (key === element.element_id || key.startsWith(`${element.element_id}_`)) {
        data[key] = preview.data[key];
      }
    });

    const normalizedText = normalizeTranslateTextAndStyles(element);
    const svgStyleRuns = buildSvgStyleRuns(element);
    const layoutLines = buildLayoutLines(element, normalizedText.text, normalizedText.styles, svgStyleRuns);

    fieldObjects[element.element_id] = {
      element_id: element.element_id,
      type: element.type,
      text: normalizedText.text,
      styles: normalizedText.styles,
      layout_lines: layoutLines,
      fill: element.fill,
      fontFamily: element.fontFamily,
      fontSize: element.fontSize,
      fontWeight: element.fontWeight,
      fontStyle: element.fontStyle,
      underline: element.underline,
      linethrough: element.linethrough,
      overline: element.overline,
      charSpacing: element.charSpacing,
      lineHeight: element.lineHeight,
      textAlign: element.textAlign,
      direction: element.direction,
      width: element.width,
      height: element.height,
      scaleX: element.scaleX,
      scaleY: element.scaleY,
      angle: element.angle,
      left: element.left,
      top: element.top,
      data,
    };
  });

  return fieldObjects;
}

async function copyAndTranslate(
    instance,
    translateFields,
    lang,
    image = null,
    shouldUpdateProjectNameByLanguage = false
) {
  const url = $(`.template-item[data-instance=${instance.id}] .dropdown-item.duplicate-template`).attr('data-url');
  
  await LAYOUT_API.copyAndTranslate(url, {
    translate_target_language: lang,
    translate_fields: translateFields,
    should_update_project_name_by_language: shouldUpdateProjectNameByLanguage,
  });
  
  const perPage = parseInt($('input[name="per_page"]').val(), 10);

  if (perPage === 1) {
    const redirectUrl = new URL(window.location.href);
    const activePage = parseInt($('input[name="current_page"]').val(), 10)
      || parseInt(redirectUrl.searchParams.get('page'), 10)
      || 1;

    redirectUrl.searchParams.set('per_page', 1);
    redirectUrl.searchParams.set('page', activePage + 1);

    return redirectUrl.toString();
  }
}

async function copyFieldsAndTranslate(instance, translateFields, lang, image) {
    const layoutPartPath = location.pathname.replace('show', '')
    const translateFieldObjects = buildTranslateFieldObjects(instance, translateFields);
  
    let translationResult = await LAYOUT_API.getTranslationForFields(layoutPartPath, instance, {
        translate_target_language: lang,
        translate_fields: translateFields,
        translate_field_objects: translateFieldObjects,
        image: image
    });

    translationResult = translationResult.data;

    const preview = store.getters['layout/selectedInstance'];
    const textElements = preview.canvas.getObjects('textbox');
  
    const newFieldOptions = {};

    for (const element of textElements) {
      const options = {};
      const newCoordinates = element.getPointByOrigin('top', 'right');
      
      const defaultFieldProperties = element.type === 'Text' 
        ? DEFAULT_TEXT_FIELD_PROPERTIES 
        : DEFAULT_MULTI_LINE_TEXT_FIELD_PROPERTIES;
  
      for (const key of Object.keys(defaultFieldProperties)) {
        for (const dataOption in preview.data) {
          if (dataOption === element.element_id + key) {
            options[key] = preview.data[dataOption];
  
            if (key === '_offset_x') {
              options[key] = newCoordinates.x;
            }
  
            if (key === '_offset_y') {
              options[key] = newCoordinates.y;
            }
          }
        }
      }
      
      options['_translated_from_element_id'] = element.element_id;
      options['text'] = translationResult[element.element_id] ?? 'No translation found.';
      options['_styles'] = translationResult[element.element_id+'_styles'] ?? null;
  
      newFieldOptions[element.element_id] = { ...options};
    }
    
    for (const key in translationResult) {
      const type = key.includes('multi_line_text') ? 'Multi Line Text' : 'Text';
      
      let alreadyTranslatedField;
      for (const previewDataKey in preview.data) {
        if (previewDataKey.includes('_translated_from_element_id') && preview.data[previewDataKey] === key) {
          alreadyTranslatedField = previewDataKey.split('_translated_from_element_id')[0];
        }
      }

      const alreadyTranslatedElement = alreadyTranslatedField
        ? textElements.find(element => element.element_id === alreadyTranslatedField)
        : null;
      
      if (!alreadyTranslatedElement) {
          const originalElement = textElements.find(element => element.element_id === key);
          if (originalElement) {
            await addAddField(type, preview.id, newFieldOptions[key]);
          }
        }
    }
    preview.canvas.renderAll()
}

async function translate(instance, translateFields, lang) {
  const layoutPartPath = location.pathname.replace('show', '')
  await LAYOUT_API.translate(layoutPartPath, instance, {
    translate_target_language: lang,
    translate_fields: translateFields
  });
}

async function translateWithoutReload(instance, translateFields, lang, image) {
    const layoutPartPath = location.pathname.replace('show', '')
    const translateFieldObjects = buildTranslateFieldObjects(instance, translateFields);

    let translationResult = await LAYOUT_API.getTranslationForFields(layoutPartPath, instance, {
      translate_target_language: lang,
      translate_fields: translateFields,
      translate_field_objects: translateFieldObjects,
      image: image
    });

    translationResult = translationResult.data;

    const preview = store.getters['layout/selectedInstance'];
    const textElements = preview.canvas.getObjects('textbox');

    const _measureCtx = (() => {
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d');
      ctx.textBaseline = 'alphabetic';
      return ctx;
    })();

    const computeMaxLineWidth = (element, text) => {
      // Measure unwrapped lines with the same character styles and kerning as
      // the textbox. Width is in local coordinates: scaleX is applied by Fabric.
      const measured = new fabric.Text(text, {
        fontFamily: element.fontFamily,
        fontSize: element.fontSize,
        fontWeight: element.fontWeight,
        fontStyle: element.fontStyle,
        charSpacing: element.charSpacing,
        lineHeight: element.lineHeight,
        styles: element.styles,
      });
      return Math.ceil(measured.calcTextWidth()) + 2;
    };

    // Helper to compute total text height after wrapping
    const computeTextHeight = (element, text, width) => {
      const ctx = _measureCtx;
      ctx.font = element._getFontDeclaration();
      const fontSize = element.fontSize || 16;
      const lineHeight = element.lineHeight || 1.16;
      const charSpacingPx = (element.charSpacing || 0) / 1000 * fontSize;

      const lines = String(text).split(/\r?\n/);
      let totalLines = 0;

      for (const paragraph of lines) {
        if (!paragraph.trim()) {
          totalLines++;
          continue;
        }

        const words = paragraph.split(/\s+/);
        let currentLine = '';

        for (const word of words) {
          const testLine = currentLine ? currentLine + ' ' + word : word;
          let testWidth = ctx.measureText(testLine).width;
          if (charSpacingPx) {
            const charCount = Array.from(testLine).length;
            const gaps = Math.max(charCount - 1, 0);
            testWidth += charSpacingPx * gaps;
          }

          if (testWidth > width && currentLine) {
            totalLines++;
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) totalLines++;
      }

      const scaleY = element.scaleY || 1;
      return totalLines * fontSize * lineHeight * scaleY;
    };

    Object.keys(translationResult).forEach(element_id => {
      let textElement = preview.data[element_id];
      if (textElement) {
        const element = textElements.find(el => el.element_id === element_id);
        if (element) {
          const value = translationResult[element_id];
          if (_.isEmpty(value)) {
            return;
          }

          const translatedText = translationResult[element_id] ?? element.text ?? 'No translation found.';

          const originalLeft = element.left;
          const originalTop = element.top;
          const originalWidth = element.width;
          const originalFontSize = element.fontSize || 16;

          // Canvas dimensions
          const canvasWidth = instance.dimension[0];
          const canvasHeight = instance.dimension[1];

          // Apply translated character styles before measuring, since they may
          // change glyph widths even when the text itself is unchanged.
          if (typeof element.fill !== 'object' && translationResult[element_id + '_styles']) {
            const styles = normalizeFabricStyles(translationResult[element_id + '_styles']);
            if (styles && styles.length > 0) {
              element.set('styles', styles);
            }
          }

          const availableWidth = Math.max(1, canvasWidth / Math.abs(element.scaleX || 1));
          const optimalTextWidth = Math.max(originalWidth, Math.min(
            computeMaxLineWidth(element, translatedText),
            availableWidth
          ));

          let adjustedWidth = optimalTextWidth;
          let newLeft = 0;

          const borderPadding = canvasWidth * 0.1;

          if (element.textAlign.startsWith('left')) {
            element.set('width', adjustedWidth);
            adjustedWidth = element.get('width');
            newLeft = originalLeft;
            if (newLeft + adjustedWidth > canvasWidth) {
              newLeft = Math.max(borderPadding, canvasWidth - adjustedWidth - borderPadding);
            }
            element.set('left', newLeft);
          } else if (element.textAlign.startsWith('center')) {
            element.set('width', adjustedWidth);
            adjustedWidth = element.get('width');
            newLeft = originalLeft - (adjustedWidth - originalWidth) / 2;
            if (newLeft < borderPadding) {
              newLeft = borderPadding;
            } else if (newLeft + adjustedWidth > canvasWidth) {
              newLeft = Math.max(borderPadding, canvasWidth - adjustedWidth - borderPadding);
            }
            element.set('left', newLeft);
          } else if (element.textAlign.startsWith('right')) {
            element.set('width', adjustedWidth);
            adjustedWidth = element.get('width');
            newLeft = originalLeft - (adjustedWidth - originalWidth);
            if (newLeft < borderPadding) {
              newLeft = borderPadding;
            }
            element.set('left', newLeft);
          }

          element.set('text', translatedText);

          // Check vertical bounds and adjust font size if needed
          const verticalBorderPadding = canvasHeight * 0.05;
          const maxAvailableHeight = canvasHeight - originalTop - verticalBorderPadding;

          let currentHeight = computeTextHeight(element, translatedText, element.get('width'));
          let currentFontSize = element.fontSize || originalFontSize;
          const minFontSize = Math.max(8, originalFontSize * 0.5); // Don't go below 50% of original or 8px

          // Reduce font size iteratively if text exceeds vertical bounds
          while (currentHeight > maxAvailableHeight && currentFontSize > minFontSize) {
            currentFontSize = Math.max(minFontSize, currentFontSize - 1);
            element.set('fontSize', currentFontSize);
            currentHeight = computeTextHeight(element, translatedText, element.get('width'));
          }

          // If still overflowing after font size reduction, adjust position if possible
          if (currentHeight > maxAvailableHeight) {
            const overflow = currentHeight - maxAvailableHeight;
            const newTop = Math.max(verticalBorderPadding, originalTop - overflow);
            element.set('top', newTop);
          }

        }
      }
    });

    preview.canvas.renderAll();
}

export async function translateTemplate({ textFields, instance, lang, handler, image }) {
  const loadingType = LOADING_TYPE[handler];

  editorControlEventBroker.fire(EVENTS.TOGGLE_LOADING, { loadingType });

  let redirectUrl;

  try {
    const translateFields = textFields.map(field => field.element_id);

    redirectUrl = await HANDLERS[handler](
      instance,
      translateFields,
      lang,
      image,
      handler === HANDLER_TYPES.COPY_AND_TRANSLATE
    );
  } finally {
    editorControlEventBroker.fire(EVENTS.TOGGLE_LOADING, { loadingType });
    $('.loadingOverlay').fadeOut();
  }

  if (handler === HANDLER_TYPES.COPY_AND_TRANSLATE) {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      location.reload();
    }
  } else {
    $('[name=language_id]').trigger('change');
  }
}

export function undoRedoTranslationResult(previews, event) {
  const data = event.target.value.translations
  const preview = previews.find(p => p.id === event.target.value.instance_id);
  if (!preview) return;
  Object.entries(data).forEach(([id, payload]) => {
    const obj = preview.canvas.getObjects().find(o => o.element_id === id);
    if (obj) {
      obj.set({ text: payload.text });
      if (payload.styles) {
        obj.set({ styles: payload.styles });
      }
    }
  });
  preview.canvas.renderAll();
}

export function extractTextAndStyles(obj) {
  return {
    text: obj.text,
    ...(obj.styles && { styles: obj.styles }),
  };
}
